import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { productsAPI } from '../api/products';
import { ProductDetail as ProductDetailType } from '../types/product';
import { useCart, CCSelectedOption } from '../context/CartContext';
import {
  fetchCCOptions,
  CCOptionGroup,
  isCCSingleSelect,
  defaultCCSelections,
  toggleCCSelection,
  hasAllRequiredCC,
} from '../api/ccOptions';
import Breadcrumb from '../components/Layout/Breadcrumb';
import { FiShoppingCart, FiChevronLeft, FiChevronRight, FiAlertCircle } from 'react-icons/fi';
import { formatCurrency } from '../utils/currency';
import { getLocalizedField } from '../utils/localize';
import CakePhotoUpload from '../components/Products/CakePhotoUpload';
import type { SupportedLanguage } from '../i18n';

const PageContainer = styled.div`
  min-height: 100vh;
  padding-top: calc(var(--header-height) + 1rem);
  padding-bottom: 2rem;
`;

const ProductContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-top: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 1024px) {
    gap: 3rem;
  }
`;

const ImageSection = styled.div`
  position: sticky;
  top: calc(var(--header-height) + 2rem);
  height: fit-content;
`;

const MainImageContainer = styled.div`
  position: relative;
  aspect-ratio: 1;
  background-color: var(--gray-light);
  border-radius: 8px;
  overflow: hidden;
`;

const MainImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ThumbnailContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--gray-light);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--gray-medium);
    border-radius: 3px;
  }
`;

const Thumbnail = styled.img<{ $active: boolean }>`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  border: 2px solid ${props => props.$active ? 'var(--primary-color)' : 'transparent'};
  opacity: ${props => props.$active ? 1 : 0.7};
  transition: all 0.3s;

  &:hover {
    opacity: 1;
    border-color: var(--primary-color);
  }
`;

const ImageNavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s;
  z-index: 1;

  &:hover {
    background: var(--white);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const PrevButton = styled(ImageNavButton)`
  left: 1rem;
`;

const NextButton = styled(ImageNavButton)`
  right: 1rem;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ProductName = styled.h1`
  color: var(--text-color);
  font-size: 2rem;
  margin: 0;
`;

const ProductDescription = styled.p`
  color: var(--gray-dark);
  line-height: 1.6;
  font-size: 1.1rem;
`;

const PriceSection = styled.div`
  background-color: var(--light-bg);
  padding: 1.5rem;
  border-radius: 8px;
`;

const Price = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const PreparationTime = styled.div`
  color: var(--gray-dark);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SizeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SizeLabel = styled.label`
  font-weight: 600;
  color: var(--text-color);
`;

const SizeOptions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SizeOption = styled.button<{ $selected: boolean }>`
  padding: 0.75rem 1.5rem;
  border: 2px solid ${props => props.$selected ? 'var(--primary-color)' : 'var(--gray-medium)'};
  background-color: ${props => props.$selected ? 'var(--primary-color)' : 'var(--white)'};
  color: ${props => props.$selected ? 'var(--white)' : 'var(--text-color)'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 1rem;

  &:hover {
    border-color: var(--primary-color);
    background-color: ${props => props.$selected ? 'var(--secondary-color)' : 'var(--light-bg)'};
  }

  small {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.85rem;
    opacity: 0.9;
  }
`;

const CustomizationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const CategoryBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CategoryLabel = styled.label`
  font-weight: 600;
  color: var(--text-color);
`;

const OptionsGrid = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const OptionTile = styled.button<{ $selected: boolean }>`
  padding: 0.75rem 1.5rem;
  border: 2px solid ${props => props.$selected ? 'var(--primary-color)' : 'var(--gray-medium)'};
  background-color: ${props => props.$selected ? 'var(--primary-color)' : 'var(--white)'};
  color: ${props => props.$selected ? 'var(--white)' : 'var(--text-color)'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 1rem;

  &:hover {
    border-color: var(--primary-color);
    background-color: ${props => props.$selected ? 'var(--secondary-color)' : 'var(--light-bg)'};
  }

  small {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.85rem;
    opacity: 0.9;
  }
`;

const QuantitySection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const QuantityLabel = styled.label`
  font-weight: 600;
  color: var(--text-color);
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const QuantityButton = styled.button`
  width: 36px;
  height: 36px;
  border: 1px solid var(--gray-medium);
  background-color: var(--white);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover:not(:disabled) {
    background-color: var(--light-bg);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 60px;
  text-align: center;
  padding: 0.5rem;
  border: 1px solid var(--gray-medium);
  border-radius: 4px;
  font-size: 1rem;
`;

const AddToCartButton = styled.button`
  background-color: var(--primary-color);
  color: var(--white);
  border: none;
  border-radius: 8px;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: background-color 0.3s;
  width: 100%;

  &:hover:not(:disabled) {
    background-color: var(--secondary-color);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const AllergySection = styled.div`
  background-color: var(--warning-bg);
  border: 1px solid var(--warning-border);
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;

  svg {
    color: var(--warning-color);
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const AllergyInfo = styled.div`
  flex: 1;
`;

const AllergyTitle = styled.h3`
  color: var(--warning-color);
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
`;

const AllergyList = styled.ul`
  margin: 0;
  padding-left: 1.5rem;
  color: var(--text-color);

  li {
    margin: 0.25rem 0;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  font-size: 1.2rem;
  color: var(--gray-dark);
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  text-align: center;
  gap: 1rem;

  h2 {
    color: var(--text-color);
  }

  p {
    color: var(--gray-dark);
  }

  button {
    padding: 0.75rem 1.5rem;
    background-color: var(--primary-color);
    color: var(--white);
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;

    &:hover {
      background-color: var(--secondary-color);
    }
  }
`;

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { t, i18n } = useTranslation();
  // Mirrors the LanguageToggle.tsx pattern: i18next's language can be a
  // region-qualified tag or unset, so narrow to the two v1-supported values.
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';

  const [product, setProduct] = useState<ProductDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState<number | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);

  // Customization (only relevant when product.customizable is true)
  const [customImageUrl, setCustomImageUrl] = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  // Cake Options (cc_* groups, task-191). Fetched for every product: the server
  // returns [] for products with no linked option groups.
  const [ccGroups, setCcGroups] = useState<CCOptionGroup[]>([]);
  const [ccLoading, setCcLoading] = useState(false);
  const [ccSelections, setCcSelections] = useState<Record<number, number[]>>({});
  const [ccLoadFailed, setCcLoadFailed] = useState(false);
  const [ccReloadKey, setCcReloadKey] = useState(0);

  useEffect(() => {
    // A photo belongs to the product it was uploaded for; never carry it across products.
    setCustomImageUrl(null);
    setPhotoUploading(false);
    loadProduct();
  }, [id]);

  useEffect(() => {
    if (!product || !product.customizable) {
      setCustomImageUrl(null);
    }
  }, [product]);

  useEffect(() => {
    const productId = product?.product_id;
    setCcLoadFailed(false);
    if (productId === undefined) {
      setCcGroups([]);
      setCcSelections({});
      return;
    }

    let cancelled = false;
    setCcLoading(true);
    fetchCCOptions(productId)
      .then(groups => {
        if (!cancelled) {
          const list = Array.isArray(groups) ? groups : [];
          setCcGroups(list);
          // Required groups start with their first item preselected.
          setCcSelections(defaultCCSelections(list));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCcGroups([]);
          setCcSelections({});
          setCcLoadFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCcLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [product?.product_id, ccReloadKey]);

  const loadProduct = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await productsAPI.getProductDetail(parseInt(id));
      setProduct(data);

      // Set default size if available
      if (data.sizes && data.sizes.length > 0) {
        setSelectedSize(0);
      }
    } catch (err) {
      console.error('Failed to load product:', err);
      setError(t('productDetail.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // Selected Cake Option items with their group, for display and the cart line.
  const ccSelectedDetails: CCSelectedOption[] = ccGroups.flatMap(group =>
    (ccSelections[group.id] ?? [])
      .map(itemId => group.items.find(i => i.id === itemId))
      .filter((item): item is CCOptionGroup['items'][number] => !!item)
      .map(item => ({
        groupId: group.id,
        groupName: group.name,
        groupName_zh: group.name_zh ?? null,
        itemId: item.id,
        name: item.name,
        name_zh: item.name_zh ?? null,
        price: item.price,
      }))
  );
  const ccPriceTotal = ccSelectedDetails.reduce((sum, o) => sum + o.price, 0);
  const ccRequiredMet = hasAllRequiredCC(ccGroups, ccSelections);
  // A failed options fetch must not let a product with required options be added blind.
  const ccBlocked = ccLoadFailed && !!product?.has_required_options;

  const handleAddToCart = () => {
    if (!product || photoUploading || !ccRequiredMet || ccLoading || ccBlocked) return;

    setIsAdding(true);

    // Calculate final price based on selected size
    let finalPrice = product.base_price;
    let sizeName = 'Standard';

    if (selectedSize !== null && product.sizes && product.sizes[selectedSize]) {
      const size = product.sizes[selectedSize];
      finalPrice += size.price_modifier;
      sizeName = size.size_name;
    }

    const photoUrl = product.customizable && customImageUrl ? customImageUrl : undefined;

    // Display price only -- the server recomputes from the selected ids.
    finalPrice += ccPriceTotal;
    const ccOptions = ccSelectedDetails.length > 0 ? ccSelectedDetails : undefined;

    if (photoUrl || ccOptions) {
      addToCart(product, quantity, sizeName, finalPrice, undefined, photoUrl, ccOptions);
    } else {
      addToCart(product, quantity, sizeName, finalPrice);
    }

    setTimeout(() => {
      setIsAdding(false);
    }, 500);
  };

  const handleQuantityChange = (delta: number) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= 99) {
      setQuantity(newQuantity);
    }
  };

  const handleNextImage = () => {
    if (product?.images) {
      setSelectedImage((prev) => (prev + 1) % product.images.length);
    }
  };

  const handlePrevImage = () => {
    if (product?.images) {
      setSelectedImage((prev) =>
        prev === 0 ? product.images.length - 1 : prev - 1
      );
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="container">
          <LoadingContainer>{t('productDetail.loadingDetails')}</LoadingContainer>
        </div>
      </PageContainer>
    );
  }

  if (error || !product) {
    return (
      <PageContainer>
        <div className="container">
          <ErrorContainer>
            <h2>{t('productDetail.notFoundTitle')}</h2>
            <p>{error || t('productDetail.notFoundMessage')}</p>
            <button onClick={() => navigate('/products')}>
              {t('productDetail.backToProducts')}
            </button>
          </ErrorContainer>
        </div>
      </PageContainer>
    );
  }

  const breadcrumbItems = [
    { label: t('common.home'), path: '/' },
    { label: t('breadcrumb.products'), path: '/products' },
    { label: getLocalizedField(product, 'name', language) as string }
  ];

  const currentImage = product.images && product.images.length > 0
    ? product.images[selectedImage].image_url
    : 'https://images.unsplash.com/photo-1578985545062-69928b1d9587';

  return (
    <PageContainer>
      <div className="container">
        <Breadcrumb items={breadcrumbItems} />

        <ProductContainer>
          <ImageSection>
            <MainImageContainer>
              {product.images && product.images.length > 1 && (
                <>
                  <PrevButton onClick={handlePrevImage}>
                    <FiChevronLeft />
                  </PrevButton>
                  <NextButton onClick={handleNextImage}>
                    <FiChevronRight />
                  </NextButton>
                </>
              )}
              <MainImage src={currentImage} alt={product.name} />
            </MainImageContainer>

            {product.images && product.images.length > 1 && (
              <ThumbnailContainer>
                {product.images.map((image, index) => (
                  <Thumbnail
                    key={image.image_id}
                    src={image.image_url}
                    alt={`${product.name} ${index + 1}`}
                    $active={index === selectedImage}
                    onClick={() => setSelectedImage(index)}
                  />
                ))}
              </ThumbnailContainer>
            )}
          </ImageSection>

          <ProductInfo>
            <div>
              <ProductName>{getLocalizedField(product, 'name', language) as string}</ProductName>
              <ProductDescription>{getLocalizedField(product, 'description', language) as string | undefined}</ProductDescription>
            </div>

            <PriceSection>
              <Price data-testid="product-price">
                {selectedSize !== null && product.sizes && product.sizes[selectedSize]
                  ? formatCurrency(product.base_price + product.sizes[selectedSize].price_modifier + ccPriceTotal)
                  : formatCurrency(product.base_price + ccPriceTotal)}
              </Price>
              <PreparationTime>
                🕐 {t('productDetail.preparationTime', { hours: product.preparation_time_hours })}
              </PreparationTime>
            </PriceSection>

            {product.sizes && product.sizes.length > 0 && (
              <SizeSection>
                <SizeLabel>{t('productDetail.selectSize')}</SizeLabel>
                <SizeOptions>
                  {product.sizes.map((size, index) => (
                    <SizeOption
                      key={size.size_id}
                      $selected={selectedSize === index}
                      onClick={() => setSelectedSize(index)}
                    >
                      {getLocalizedField(size, 'size_name', language) as string}
                      {size.price_modifier > 0 && (
                        <small>+{formatCurrency(size.price_modifier)}</small>
                      )}
                    </SizeOption>
                  ))}
                </SizeOptions>
              </SizeSection>
            )}

            {(ccLoading || ccGroups.length > 0 || ccLoadFailed) && (
              <CustomizationSection data-testid="product-cc-options-section">
                {ccLoading && <p>{t('productDetail.loadingOptions')}</p>}
                {!ccLoading && ccGroups.map(group => {
                  const single = isCCSingleSelect(group);
                  const current = ccSelections[group.id] ?? [];
                  return (
                    <CategoryBlock
                      key={group.id}
                      role={single ? 'radiogroup' : 'group'}
                      aria-label={getLocalizedField(group, 'name', language) as string}
                      data-testid={`cc-group-${group.id}`}
                    >
                      <CategoryLabel>
                        {getLocalizedField(group, 'name', language) as string}
                        {group.required && <small> {t('productDetail.ccRequired')}</small>}
                      </CategoryLabel>
                      <OptionsGrid>
                        {group.items.map(item => {
                          const isSelected = current.includes(item.id);
                          return (
                            <OptionTile
                              key={item.id}
                              type="button"
                              $selected={isSelected}
                              data-testid={`cc-option-${item.id}`}
                              onClick={() => setCcSelections(prev => toggleCCSelection(prev, group, item.id))}
                              role={single ? 'radio' : 'checkbox'}
                              aria-checked={isSelected}
                            >
                              {getLocalizedField(item, 'name', language) as string}
                              {item.price !== 0 && (
                                <small>+{formatCurrency(item.price)}</small>
                              )}
                            </OptionTile>
                          );
                        })}
                      </OptionsGrid>
                    </CategoryBlock>
                  );
                })}
                {!ccLoading && ccLoadFailed && (
                  <div>
                    <p role="alert" data-testid="cc-load-error">{t('productDetail.ccLoadError')}</p>
                    <button type="button" data-testid="cc-load-retry" onClick={() => setCcReloadKey(k => k + 1)}>
                      {t('productDetail.ccRetry')}
                    </button>
                  </div>
                )}
                {!ccLoading && !ccLoadFailed && !ccRequiredMet && (
                  <p role="alert" data-testid="cc-required-hint">{t('productDetail.ccSelectRequired')}</p>
                )}
              </CustomizationSection>
            )}

            {product.customizable && (
              <CustomizationSection data-testid="product-customization-section">
                <CakePhotoUpload value={customImageUrl} onChange={setCustomImageUrl} onUploadingChange={setPhotoUploading} />
              </CustomizationSection>
            )}

            <QuantitySection>
              <QuantityLabel>{t('productDetail.quantity')}</QuantityLabel>
              <QuantityControls>
                <QuantityButton
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity <= 1}
                >
                  -
                </QuantityButton>
                <QuantityInput
                  type="number"
                  value={quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (!isNaN(val) && val >= 1 && val <= 99) {
                      setQuantity(val);
                    }
                  }}
                  min="1"
                  max="99"
                />
                <QuantityButton
                  onClick={() => handleQuantityChange(1)}
                  disabled={quantity >= 99}
                >
                  +
                </QuantityButton>
              </QuantityControls>
            </QuantitySection>

            <AddToCartButton
              onClick={handleAddToCart}
              disabled={isAdding || photoUploading || !ccRequiredMet || ccLoading || ccBlocked}
              data-testid="add-to-cart-button"
            >
              <FiShoppingCart />
              {isAdding ? t('productDetail.addingToCart') : t('productDetail.addToCart')}
            </AddToCartButton>

            {product.allergies && product.allergies.length > 0 && (
              <AllergySection>
                <FiAlertCircle />
                <AllergyInfo>
                  <AllergyTitle>{t('productDetail.allergenInformation')}</AllergyTitle>
                  <AllergyList>
                    {product.allergies.map((allergy, index) => (
                      <li key={index}>
                        {t('productDetail.allergyContains', { allergy: t(`allergy.${allergy}`, { defaultValue: allergy.charAt(0).toUpperCase() + allergy.slice(1) }) })}
                      </li>
                    ))}
                  </AllergyList>
                </AllergyInfo>
              </AllergySection>
            )}
          </ProductInfo>
        </ProductContainer>
      </div>
    </PageContainer>
  );
};

export default ProductDetailPage;